import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Loader2, ArrowLeft, FileText } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLUG_FALLBACKS = {
  terms: { title: "Terms of Service" },
  privacy: { title: "Privacy Policy" },
};

export default function LegalDocument() {
  const params = useParams();
  const navigate = useNavigate();
  const slug = (params.slug || "terms").toLowerCase();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/legal/${slug}`)
      .then((res) => {
        if (!cancelled) setDoc(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || "Document not found");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <header className="border-b bg-white/70 dark:bg-slate-900/70 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="legal-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <FileText className="h-4 w-4" />
            {doc?.title || SLUG_FALLBACKS[slug]?.title || "Legal"}
          </div>
          <div className="text-xs text-muted-foreground">
            {doc?.updated_at ? `v${doc.version} · ${new Date(doc.updated_at).toLocaleDateString()}` : ""}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10 max-w-3xl" data-testid={`legal-page-${slug}`}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/")}>Go home</Button>
          </div>
        ) : (
          <article
            className="prose prose-slate dark:prose-invert max-w-none"
            data-testid="legal-content"
          >
            <ReactMarkdown>{doc?.content_md || ""}</ReactMarkdown>
          </article>
        )}

        <footer className="mt-12 pt-6 border-t text-xs text-muted-foreground flex flex-wrap gap-4 justify-center">
          <Link to="/legal/terms" className="hover:text-foreground" data-testid="footer-terms-link">Terms of Service</Link>
          <Link to="/legal/privacy" className="hover:text-foreground" data-testid="footer-privacy-link">Privacy Policy</Link>
        </footer>
      </main>
    </div>
  );
}
